/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import {
  ActivityInjectionProvider,
  useCourseHomeRedesign,
  useLoadInjectableActivities,
} from '@coursera-web/ondemand-common';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { LoadingSection } from '@coursera/cds-core';
import { CenterBox } from '@coursera/coursera-ui';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import { useCourseStoresLoading } from 'bundles/course-v2/hooks/useCourseStoresLoading';
import { useLoadComputedModels } from 'bundles/course-v2/hooks/useLoadComputedModels';
import { useLoadCourseDeadlines } from 'bundles/course-v2/hooks/useLoadCourseDeadlines';
import { useLoadCourseSessionGroup } from 'bundles/course-v2/hooks/useLoadCourseSessionGroup';
import { useLoadCourseStores } from 'bundles/course-v2/hooks/useLoadCourseStores';
import { useLoadVerificationDisplay } from 'bundles/course-v2/hooks/useLoadVerificationDisplay';
import { useCustomLabelsByUserAndCourse } from 'bundles/custom-labels/hoc/withCustomLabelsByUserAndCourse';
import CourseStore from 'bundles/in-course-stores/stores/CourseStore';

import _t from 'i18n!nls/course-v2';

const LEGACY_LOADER_HEIGHT = '512px';
const COURSE_CHROME_VIEWPORT_OFFSET = '160px';

const styles = {
  loadingContainer: css`
    width: 100%;

    /*
     * Preserve the legacy loader area while accounting for course page chrome
     * on short viewports, where forcing the full height makes the loader feel off-center.
     */
    min-height: min(${LEGACY_LOADER_HEIGHT}, calc(100vh - ${COURSE_CHROME_VIEWPORT_OFFSET}));
    padding: var(--cds-spacing-400) 0;
  `,
};

const CourseDataProvider = ({ children }: React.PropsWithChildren) => {
  useLoadComputedModels();
  useLoadVerificationDisplay();
  useLoadCourseDeadlines();
  useLoadCourseSessionGroup();
  useLoadCourseStores();

  const { loading } = useCourseStoresLoading();
  if (loading) {
    return null;
  }

  return children;
};

const CourseProvider = ({ children }: React.PropsWithChildren) => {
  const { loading: isCourseStoresLoading } = useCourseStoresLoading();
  const isCourseHomeRedesign = useCourseHomeRedesign();

  const { courseId } = useFluxibleStore(CourseStore, (store) => ({ courseId: store.getCourseId() }));
  useCustomLabelsByUserAndCourse({ courseId });

  // If there are any injectable activities, we need to load them into the
  // CourseStore before the course materials are loaded.
  const { loading: isInjectableActivitiesLoading, injectableActivities } = useLoadInjectableActivities(courseId);

  return (
    <div data-testid="course-provider">
      {!isInjectableActivitiesLoading && (
        <ActivityInjectionProvider injectableActivities={injectableActivities}>
          <CourseDataProvider>{children}</CourseDataProvider>
        </ActivityInjectionProvider>
      )}
      {(isCourseStoresLoading || isInjectableActivitiesLoading) &&
        (isCourseHomeRedesign ? (
          <div css={styles.loadingContainer}>
            <LoadingSection hideOverlay label={_t('Loading...')} />
          </div>
        ) : (
          <CenterBox height={512}>
            <SvgLoaderSignal />
          </CenterBox>
        ))}
    </div>
  );
};

const DeferToClientSideRender = ({ children }: React.PropsWithChildren) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  return <CourseProvider>{children}</CourseProvider>;
};

export default DeferToClientSideRender;
